import React, { useCallback, useMemo } from 'react';

import { Button, FormControl, Grid, InputAdornment, InputLabel, makeStyles, MenuItem, Select, TextField, Typography } from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { Link } from 'react-router-dom';

import { resourcePrivacy, templatePrivacyFilterList } from '../../models/Resource';
import { deepCopy } from '../../utils/lodashUtils';

const useStyle = makeStyles(() => {
  return {
    filtersContainer: {
      alignItems: 'center',
      display: 'flex',
      justifyContent: 'space-between',
    },
    header: {
      marginRight: '4px',
    },
    headerContainer: {
      alignItems: 'center',
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: 'space-between',
    },
    headerContainerLeft: {
      alignItems: 'center',
      display: 'flex',
      flexWrap: 'wrap',
      maxWidth: '500px',
      width: '100%',
    },
    headerContainerRight: {
      alignItems: 'end',
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: 'flex-end',
      maxWidth: '500px',
      width: '100%',
    },
    selectListWithBorder: {
      border: '1px solid #CCC',
      borderRadius: '8px',
    },
  };
});

export default ({
  selectedPrivacy,
  selectedTags,
  handleChangePrivacy,
  handleChangeSearch,
  handleChangeTag,
  handleSearch,
  handleSearchKeyDown,
  loading,
  isTagsLoading,
  search,
  tags,
  onClear,
  isMediphanyWorkspace,
}) => {
  const classes = useStyle();

  const handleRenderPrivacyValue = useCallback((v) => {
    if (!v) return 'All';

    if (v === resourcePrivacy.GLOBAL) return 'Global Collections';
    if (v === resourcePrivacy.WORKSPACE) return 'Workspace Only';
    if (v === resourcePrivacy.PRIVATE) return 'My Collections';
    return 'All';
  }, []);

  const isClearAvailable = useMemo(() => {
    if (loading) {
      return false;
    }

    return !!search || !!selectedTags || !!selectedPrivacy;
  }, [search, selectedTags, selectedPrivacy, loading]);

  const managedPrivacyList = useMemo(() => {
    const privacyList = deepCopy(templatePrivacyFilterList);
    if (isMediphanyWorkspace) {
      return privacyList.filter(({ value }) => value !== resourcePrivacy.WORKSPACE);
    }

    return privacyList;
  }, [isMediphanyWorkspace]);

  return (
    <Grid container>
      {/* HEADER */}
      <Grid item xs={12}>
        <div className={classes.headerContainer}>
          <Typography className={`page-header mb-0 ${classes.header}`}>Collections</Typography>
          <Link to="/collection-new">
            <Button color="primary" variant="contained">
              Create New Collection
            </Button>
          </Link>
        </div>
      </Grid>
      {/* HEADER END */}

      {/* FILTERS */}
      <Grid item xs={12}>
        <div className={classes.filtersContainer}>
          <div className={classes.headerContainerLeft}>
            <div style={{ marginRight: '12px', maxWidth: '200px', width: '100%' }}>
              <Autocomplete
                renderInput={(params) => <TextField {...params} label="Tags" />}
                value={selectedTags}
                options={tags}
                style={{ maxWidth: '200px', width: '100%' }}
                classes={{ popper: classes.selectListWithBorder }}
                onChange={handleChangeTag}
                disabled={loading || isTagsLoading}
              />
            </div>

            <div style={{ maxWidth: '200px', width: '100%' }}>
              <FormControl disabled={loading} style={{ maxWidth: '200px', width: '100%' }}>
                <InputLabel htmlFor="template-list-filter">Privacy</InputLabel>
                <Select
                  id="template-list-filter"
                  displayEmpty
                  renderValue={handleRenderPrivacyValue}
                  value={selectedPrivacy || 'All'}
                  style={{ maxWidth: '200px', width: '100%' }}
                  MenuProps={{
                    classes: {
                      list: classes.selectListWithBorder,
                    },
                  }}
                  label="Privacy"
                  onChange={handleChangePrivacy}>
                  <MenuItem value="">All</MenuItem>
                  {managedPrivacyList.map(({ label, value }) => (
                    <MenuItem value={value} key={`template-filter--${value}`}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
          </div>

          <div className={classes.headerContainerRight}>
            <div style={{ marginRight: '4px', maxWidth: '400px', width: '100%' }}>
              <TextField
                value={search}
                onChange={handleChangeSearch}
                placeholder="Search by title..."
                onKeyDown={handleSearchKeyDown}
                disabled={loading}
                label="Search"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <SearchIcon onClick={handleSearch} style={{ cursor: 'pointer' }} />
                    </InputAdornment>
                  ),
                }}
                style={{ maxWidth: '400px', width: '100%' }}
              />
            </div>

            {isClearAvailable ? (
              <Button color="primary" variant="outlined" onClick={onClear} disabled={loading}>
                CLEAR
              </Button>
            ) : null}
          </div>
        </div>
      </Grid>
      {/* FILTERS END */}
    </Grid>
  );
};
